import {
    ButtonGradientPrimary,
    ButtonSecondary,
    Colors,
    ConfirmDialog,
    DateInput,
    Icon,
    ModalDialog,
    SQUARE_DIMENSIONS,
    Tabs,
    TextInput,
} from '@amelio/ui-library';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAtomValue } from 'jotai';
import { useEffect, useId, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { createObjectivePeriodSchema, NAME_MAX_LENGTH } from './validation';

import type { ObjectivePeriodFormData } from './validation';
import type { ObjectivePeriodStatusType } from '@/constants/ObjectivePeriodStatus';
import type { ObjectivePeriod, ObjectivePeriodRequest } from '@/types/objective-periods';

import { companyAtom } from '@/atoms/company';
import { userInstanceAtom } from '@/atoms/userDetails';
import ValueTranslationsTab from '@/components/AddEditValueModal/ValueTranslationsTab';
import { FieldErrorMessage } from '@/components/FieldErrorMessage/FieldErrorMessage';
import StatusRadioTiles from '@/components/StatusRadioTiles/StatusRadioTiles';
import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog/UnsavedChangesDialog';
import {
    OBJECTIVE_PERIOD_CREATION_STATUSES,
    OBJECTIVE_PERIOD_DEFAULT_CREATION_STATUS,
    OBJECTIVE_PERIOD_STATUS_TRANSITIONS,
} from '@/constants/ObjectivePeriodStatus';
import { useSelectedAppLanguage } from '@/hooks';
import i18n from '@/plugins/i18n';
import { parseIsoDate, toDateInputLang, toIsoDate } from '@/utils/dateUtils';
import { overlappingPeriodNames } from '@/utils/objectivePeriodOverlap';
import {
    getObjectivePeriodPillStyle,
    getObjectivePeriodStatusConfirmation,
    getObjectivePeriodStatusLabelKey,
} from '@/utils/objectivePeriodStatus';
import { validationMessageKey } from '@/utils/validationMessage';

interface AddEditObjectivePeriodModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (request: ObjectivePeriodRequest) => Promise<void>;
    period?: ObjectivePeriod;
    existingPeriods?: ObjectivePeriod[];
    isSaving?: boolean;
}

type ObjectivePeriodTab = 'details' | 'translations';

type TranslationsByLanguage = Record<string, Record<string, string>>;

// Both tabs are siblings in the same row -- one size keeps their icons visually aligned instead
// of the pair silently drifting apart.
const TAB_ICON_SIZE = 'md';

// The confirmations open over the form, which the library stacks at 50.
const CONFIRMATION_Z_INDEX = 60;

const EMPTY_FORM: ObjectivePeriodFormData = {
    name: '',
    startDate: '',
    endDate: '',
    status: OBJECTIVE_PERIOD_DEFAULT_CREATION_STATUS,
};

const FORM_FIELDS = Object.keys(EMPTY_FORM) as (keyof ObjectivePeriodFormData)[];

/**
 * The statuses the status field may offer: on creation a period starts as a draft or goes live
 * right away; on edit, only the status it already holds plus the transitions the backend accepts
 * from it -- so the field can never build a request the API would refuse with a 409.
 */
const availableStatuses = (
    currentStatus: ObjectivePeriodStatusType | undefined,
): readonly ObjectivePeriodStatusType[] => {
    if (!currentStatus) {
        return OBJECTIVE_PERIOD_CREATION_STATUSES;
    }

    return [currentStatus, ...(OBJECTIVE_PERIOD_STATUS_TRANSITIONS[currentStatus] ?? [])];
};

export default function AddEditObjectivePeriodModal({
    isOpen,
    onClose,
    onSave,
    period,
    existingPeriods = [],
    isSaving = false,
}: AddEditObjectivePeriodModalProps) {
    const { t } = useTranslation();
    const company = useAtomValue(companyAtom);
    const user = useAtomValue(userInstanceAtom);
    const configuredLanguages = company?.company?.availableLanguages || user?.surveyLanguages || [];
    const currentLanguage = useSelectedAppLanguage();

    const [activeTab, setActiveTab] = useState<ObjectivePeriodTab>('details');
    const [translations, setTranslations] = useState<TranslationsByLanguage>({});
    const [translationsValid, setTranslationsValid] = useState(true);
    // A status change the row's menu confirms (deactivating, completing) is confirmed here too,
    // before the save leaves: the modal must not be the unguarded way around that dialog.
    const [pendingSave, setPendingSave] = useState<ObjectivePeriodFormData | null>(null);
    const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);

    const isEdit = !!period;
    const dateLang = toDateInputLang(i18n.language);

    const {
        control,
        handleSubmit,
        reset,
        watch,
        getFieldState,
        formState: { errors, isSubmitting },
    } = useForm<ObjectivePeriodFormData>({
        resolver: zodResolver(
            createObjectivePeriodSchema({
                existingPeriods,
                currentPeriodId: period?.id,
                currentLanguage,
            }),
        ),
        mode: 'onChange',
        defaultValues: EMPTY_FORM,
    });

    // The parent only learns of the save once validation has run; until then, react-hook-form's own
    // flag is what keeps a second click from sending the same period twice.
    const isBusy = isSaving || isSubmitting;

    useEffect(() => {
        if (!isOpen) return;

        setActiveTab('details');
        setPendingSave(null);
        setIsConfirmingDiscard(false);

        if (!period) {
            reset(EMPTY_FORM);
            setTranslations({});
            return;
        }

        reset({
            name: period.name?.[currentLanguage] || '',
            startDate: period.startDate,
            endDate: period.endDate,
            status: period.status,
        });

        const loadedTranslations: TranslationsByLanguage = {};
        Object.entries(period.name ?? {}).forEach(([language, text]) => {
            if (language !== currentLanguage) {
                loadedTranslations[language] = { name: text };
            }
        });
        setTranslations(loadedTranslations);
    }, [isOpen, period, reset, currentLanguage]);

    const buildNameDictionary = (currentValue: string): Record<string, string> => {
        const dictionary: Record<string, string> = {};
        const trimmed = currentValue.trim();

        if (trimmed) {
            dictionary[currentLanguage] = trimmed;
        }

        Object.entries(translations).forEach(([language, fields]) => {
            const translated = fields.name?.trim();
            if (translated) {
                dictionary[language] = translated;
            }
        });

        return dictionary;
    };

    const save = (data: ObjectivePeriodFormData) =>
        onSave({
            name: buildNameDictionary(data.name),
            startDate: data.startDate,
            endDate: data.endDate,
            status: data.status,
        });

    const handleSave = async (data: ObjectivePeriodFormData) => {
        if (
            period &&
            data.status !== period.status &&
            getObjectivePeriodStatusConfirmation(data.status)
        ) {
            setPendingSave(data);
            return;
        }

        await save(data);
    };

    const handleConfirmSave = async () => {
        if (!pendingSave) return;

        await save(pendingSave);
        setPendingSave(null);
    };

    // Changed means different from what the form opened with, for the fields as for the
    // translations: a value typed and then put back is no change. The field state is read live: the
    // rendered `isDirty` only catches up once the last keystroke's validation has settled.
    const hasChanges = () => {
        const openedNames = period?.name ?? {};

        return (
            FORM_FIELDS.some((field) => getFieldState(field).isDirty) ||
            Object.entries(translations).some(
                ([language, fields]) => (fields.name ?? '') !== (openedNames[language] ?? ''),
            )
        );
    };

    // Whatever was typed is lost on close, so closing a changed form asks first; an untouched one
    // closes at once.
    const requestClose = () => {
        if (hasChanges()) {
            setIsConfirmingDiscard(true);
            return;
        }

        onClose();
    };

    const pendingConfirmation = pendingSave
        ? getObjectivePeriodStatusConfirmation(pendingSave.status)
        : undefined;

    const statusLabelId = useId();
    const statusOptions = availableStatuses(period?.status).map((status) => ({
        value: status,
        label: t(getObjectivePeriodStatusLabelKey(status)),
        pill: getObjectivePeriodPillStyle(status),
    }));

    const translateDateError = (message: string | undefined) =>
        message && t(validationMessageKey(message));

    // Each calendar is bounded by the other date, so an inverted range cannot be picked from either.
    const startDay = parseIsoDate(watch('startDate'));
    const endDay = parseIsoDate(watch('endDate'));
    // Said, not refused: overlapping windows are allowed, and an objective across two cycles shows
    // under each. Worth knowing while the dates are being picked, not a reason to block the save.
    const overlappedPeriods = overlappingPeriodNames({
        startDate: watch('startDate'),
        endDate: watch('endDate'),
        existingPeriods,
        currentPeriodId: period?.id,
    });

    const tabItems = [
        {
            key: 'details',
            label: (
                <div className={'flex items-center gap-2'}>
                    <Icon
                        name={'edit-document-text'}
                        size={TAB_ICON_SIZE}
                        color={'inherit'}
                        ariaHidden
                    />
                    {t('objectivePeriods.detailsTab')}
                </div>
            ),
            dataTestId: 'objective-period-details-tab',
        },
        {
            key: 'translations',
            label: (
                <div className={'flex items-center gap-2'}>
                    <Icon
                        name={'character-translate'}
                        size={TAB_ICON_SIZE}
                        color={'inherit'}
                        ariaHidden
                    />
                    {t('objectivePeriods.translationsTab')}
                </div>
            ),
            dataTestId: 'objective-period-translations-tab',
        },
    ];

    return (
        <>
            <ModalDialog
                isOpen={isOpen}
                onClose={requestClose}
                // Escape reaches every open modal: while a confirmation is up, it only closes that.
                preventClose={isSaving || !!pendingSave || isConfirmingDiscard}
                title={isEdit ? t('objectivePeriods.editPeriod') : t('objectivePeriods.addPeriod')}
                subtitle={t('objectivePeriods.modalSubtitle')}
                titleIcon={
                    <span
                        className={`bg-base-50 flex ${SQUARE_DIMENSIONS.md} flex-shrink-0 items-center justify-center rounded-lg`}
                    >
                        <Icon
                            name={'time-calendar'}
                            size={'md'}
                            color={Colors.PRIMARY}
                            ariaHidden
                        />
                    </span>
                }
                size={'md'}
                closeAriaLabel={t('common.close')}
                backdropBlur
                dataTestId={'objective-period-modal'}
                onSubmit={handleSubmit(handleSave)}
                headerExtra={
                    <Tabs
                        activeKey={activeTab}
                        onChange={(key) => setActiveTab(key as ObjectivePeriodTab)}
                        items={tabItems}
                    />
                }
                footer={
                    <div className={'flex justify-end gap-3'}>
                        <ButtonSecondary
                            size={'md'}
                            onClick={requestClose}
                            disabled={isSaving}
                            dataTestId={'objective-period-cancel'}
                        >
                            {t('objectivePeriods.cancel')}
                        </ButtonSecondary>

                        <ButtonGradientPrimary
                            type={'submit'}
                            size={'md'}
                            disabled={!translationsValid || isBusy}
                            loading={isBusy}
                            // The library swaps the label for a spinner while saving; the name holds.
                            ariaLabel={
                                isEdit ? t('objectivePeriods.save') : t('objectivePeriods.add')
                            }
                            dataTestId={'objective-period-save'}
                        >
                            {isEdit ? t('objectivePeriods.save') : t('objectivePeriods.add')}
                        </ButtonGradientPrimary>
                    </div>
                }
            >
                {activeTab === 'details' ? (
                    <div className={'flex flex-col gap-6'}>
                        <div className={'flex flex-col gap-2'}>
                            <label
                                htmlFor={'objective-period-name'}
                                className={'text-navy text-md font-medium'}
                            >
                                {t('objectivePeriods.nameLabel')}{' '}
                                <span className={'text-error'}>*</span>
                            </label>

                            <Controller
                                name={'name'}
                                control={control}
                                render={({ field, fieldState }) => (
                                    <TextInput
                                        id={'objective-period-name'}
                                        value={field.value}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        placeholder={t('objectivePeriods.namePlaceholder')}
                                        maxLength={NAME_MAX_LENGTH}
                                        showCharacterCount
                                        variant={'white'}
                                        error={!!fieldState.error}
                                        errorMessage={
                                            fieldState.error?.message
                                                ? t(
                                                      validationMessageKey(
                                                          fieldState.error.message,
                                                      ),
                                                      {
                                                          max: NAME_MAX_LENGTH,
                                                      },
                                                  )
                                                : undefined
                                        }
                                        dataTestId={'objective-period-name-input'}
                                    />
                                )}
                            />
                        </div>

                        <div className={'flex flex-col gap-3 sm:flex-row'}>
                            <div
                                role={'group'}
                                aria-label={t('objectivePeriods.startDateLabel')}
                                className={'flex flex-1 flex-col gap-2'}
                            >
                                <span className={'text-navy text-md font-medium'}>
                                    {t('objectivePeriods.startDateLabel')}{' '}
                                    <span className={'text-error'}>*</span>
                                </span>

                                <Controller
                                    name={'startDate'}
                                    control={control}
                                    // The order rule is reported under the end date: a new start
                                    // must re-check it, or the message would wait for the submit.
                                    rules={{ deps: 'endDate' }}
                                    render={({ field }) => (
                                        <DateInput
                                            value={field.value || undefined}
                                            onChange={(date) =>
                                                field.onChange(date ? toIsoDate(date) : '')
                                            }
                                            disabledDates={endDay ? { after: endDay } : undefined}
                                            lang={dateLang}
                                            placeholder={t('objectivePeriods.startDateLabel')}
                                            ariaLabel={t('objectivePeriods.startDateLabel')}
                                            clearable
                                            portalPanel
                                            dataTestId={'objective-period-start-date-input'}
                                        />
                                    )}
                                />

                                <FieldErrorMessage
                                    message={translateDateError(errors.startDate?.message)}
                                />
                            </div>

                            <div
                                role={'group'}
                                aria-label={t('objectivePeriods.endDateLabel')}
                                className={'flex flex-1 flex-col gap-2'}
                            >
                                <span className={'text-navy text-md font-medium'}>
                                    {t('objectivePeriods.endDateLabel')}{' '}
                                    <span className={'text-error'}>*</span>
                                </span>

                                <Controller
                                    name={'endDate'}
                                    control={control}
                                    render={({ field }) => (
                                        <DateInput
                                            value={field.value || undefined}
                                            onChange={(date) =>
                                                field.onChange(date ? toIsoDate(date) : '')
                                            }
                                            disabledDates={
                                                startDay ? { before: startDay } : undefined
                                            }
                                            lang={dateLang}
                                            placeholder={t('objectivePeriods.endDateLabel')}
                                            ariaLabel={t('objectivePeriods.endDateLabel')}
                                            clearable
                                            portalPanel
                                            dataTestId={'objective-period-end-date-input'}
                                        />
                                    )}
                                />

                                <FieldErrorMessage
                                    message={translateDateError(errors.endDate?.message)}
                                />
                            </div>
                        </div>

                        {overlappedPeriods.length > 0 && (
                            <p
                                role={'status'}
                                className={'text-warning text-sm'}
                                data-testid={'objective-period-overlap-warning'}
                            >
                                {t('objectivePeriods.overlapWarning', {
                                    count: overlappedPeriods.length,
                                    periods: overlappedPeriods.join(', '),
                                })}
                            </p>
                        )}

                        <div className={'flex flex-col gap-2'}>
                            <span
                                id={statusLabelId}
                                className={'text-navy text-md font-medium'}
                            >
                                {t('objectivePeriods.statusLabel')}
                            </span>

                            <Controller
                                name={'status'}
                                control={control}
                                render={({ field }) => (
                                    <StatusRadioTiles
                                        name={'objective-period-status'}
                                        labelledBy={statusLabelId}
                                        options={statusOptions}
                                        value={field.value}
                                        onChange={field.onChange}
                                        dataTestId={'objective-period-status'}
                                    />
                                )}
                            />
                        </div>
                    </div>
                ) : (
                    <ValueTranslationsTab
                        valueId={null}
                        questions={[]}
                        configuredLanguages={configuredLanguages}
                        currentLanguage={currentLanguage}
                        initialTranslations={translations}
                        onTranslationsChange={setTranslations}
                        onValidationChange={setTranslationsValid}
                        nameLabel={'objectivePeriods.nameLabel'}
                        nameMaxLength={NAME_MAX_LENGTH}
                        nameMaxLengthMessage={'objectivePeriods.nameMaxLength'}
                        showDescription={false}
                    />
                )}
            </ModalDialog>

            {pendingConfirmation && pendingSave && (
                <ConfirmDialog
                    isOpen
                    title={t(pendingConfirmation.title)}
                    message={t(pendingConfirmation.message, { name: pendingSave.name })}
                    confirmLabel={t(pendingConfirmation.confirmLabel)}
                    cancelLabel={t('objectivePeriods.cancel')}
                    onConfirm={handleConfirmSave}
                    onCancel={() => setPendingSave(null)}
                    isLoading={isSaving}
                    zIndex={CONFIRMATION_Z_INDEX}
                    dataTestId={'objective-period-status-confirm'}
                />
            )}

            <UnsavedChangesDialog
                isOpen={isConfirmingDiscard}
                onConfirm={() => {
                    setIsConfirmingDiscard(false);
                    onClose();
                }}
                onCancel={() => setIsConfirmingDiscard(false)}
                zIndex={CONFIRMATION_Z_INDEX}
                cancelLabel={t('common.unsavedChanges.keepEditing')}
            />
        </>
    );
}
